import { existsSync } from "fs";
import { resolve } from "path";
import type { GencowProcedureDef } from "@gencow/core";
import { collectDefineApiCronJobs, hasDefineApiCrons } from "./cron-schedule-collect.js";
import {
  executeScheduledAction,
  ScheduledActionNotFoundError,
  type ScheduledActionMaps,
} from "./scheduled-action-resolver.js";

interface SchedulerLike {
  cron(name: string, pattern: string, handler: () => Promise<unknown>): void;
  executeActionStrict(name: string): Promise<unknown>;
}

interface MutationLike {
  argsSchema?: unknown;
  handler(ctx: unknown, args: unknown): Promise<unknown>;
}

interface CronJobLike {
  name: string;
  pattern: string;
  action: string | (() => Promise<unknown>) | { kind: string; name?: string };
}

interface LoggerLike {
  log(...args: unknown[]): void;
  warn(...args: unknown[]): void;
  error(...args: unknown[]): void;
}

export function resolveDeclarativeCronsFile(
  functionsPath: string,
  fileExists: (path: string) => boolean = existsSync,
): string | null {
  const cronsPath = resolve(functionsPath, "crons.ts");
  const cronsPathJs = resolve(functionsPath, "crons.js");
  return fileExists(cronsPath) ? cronsPath : fileExists(cronsPathJs) ? cronsPathJs : null;
}

export function createDeclarativeCronHandler(params: {
  scheduler: SchedulerLike;
  job: CronJobLike;
  mutationMap: Map<string, MutationLike>;
  internalProcedureMap?: Map<string, GencowProcedureDef<"internal", any, any, any>>;
  mutationProcedureMap?: Map<string, GencowProcedureDef<"mutation", any, any, any>>;
  buildCtx: () => unknown;
  parseArgs?: (schema: unknown, args: unknown) => unknown;
  executeWithTimeout: (fn: () => Promise<unknown>, type: "cron", functionName: string) => Promise<unknown>;
  log?: LoggerLike;
}): () => Promise<unknown> {
  if (typeof params.job.action === "function") {
    return params.job.action;
  }

  const actionName =
    typeof params.job.action === "string"
      ? params.job.action
      : (params.job.action as { name?: string }).name;
  if (!actionName) {
    throw new Error(`Cron job "${params.job.name}" action must be a string or procedure.internal ref`);
  }
  const log = params.log ?? console;
  const maps: ScheduledActionMaps = {
    internalProcedureMap: params.internalProcedureMap ?? new Map(),
    mutationProcedureMap: params.mutationProcedureMap ?? new Map(),
    mutationMap: params.mutationMap as ScheduledActionMaps["mutationMap"],
  };
  return async () => {
    try {
      await executeScheduledAction({
        action: actionName,
        rawArgs: {},
        maps,
        buildCtx: params.buildCtx as () => any,
        parseArgs: params.parseArgs,
        executeWithTimeout: params.executeWithTimeout,
        invoker: "cron",
      });
      log.log(`[scheduler] Cron action "${actionName}" completed`);
    } catch (err: unknown) {
      if (err instanceof ScheduledActionNotFoundError) {
        await params.scheduler.executeActionStrict(actionName);
        return;
      }
      const msg = err instanceof Error ? err.message : JSON.stringify(err, null, 2);
      const stack = err instanceof Error ? err.stack : undefined;
      log.error(`[scheduler] Cron action "${actionName}" failed:`, msg);
      if (stack) {
        log.error(stack);
      }
      throw err;
    }
  };
}

export async function registerDeclarativeCronJobs(params: {
  scheduler: SchedulerLike;
  functionsPath: string;
  appModule?: unknown;
  mutationMap: Map<string, MutationLike>;
  internalProcedureMap?: Map<string, GencowProcedureDef<"internal", any, any, any>>;
  mutationProcedureMap?: Map<string, GencowProcedureDef<"mutation", any, any, any>>;
  buildCtx: () => unknown;
  parseArgs?: (schema: unknown, args: unknown) => unknown;
  executeWithTimeout: (fn: () => Promise<unknown>, type: "cron", functionName: string) => Promise<unknown>;
  isBaas: boolean;
  fileExists?: (path: string) => boolean;
  importModule?: (path: string) => Promise<{ default?: { getJobs?: () => CronJobLike[] } }>;
  log?: LoggerLike;
}): Promise<void> {
  const log = params.log ?? console;
  const cronsFile = resolveDeclarativeCronsFile(params.functionsPath, params.fileExists);
  const hasApiCrons = params.appModule ? hasDefineApiCrons(params.appModule) : false;

  if (params.isBaas) {
    if (cronsFile || hasApiCrons) {
      log.log(`[crons] BaaS mode — skipping local cron scheduling (Platform Cron Runner handles it)`);
    }
    return;
  }

  if (hasApiCrons) {
    const jobs = collectDefineApiCronJobs(params.appModule);
    for (const job of jobs) {
      params.scheduler.cron(
        job.name,
        job.pattern,
        createDeclarativeCronHandler({
          scheduler: params.scheduler,
          job,
          mutationMap: params.mutationMap,
          internalProcedureMap: params.internalProcedureMap,
          mutationProcedureMap: params.mutationProcedureMap,
          buildCtx: params.buildCtx,
          parseArgs: params.parseArgs,
          executeWithTimeout: params.executeWithTimeout,
          log,
        }),
      );
    }
    log.log(`[crons] Loaded ${jobs.length} declarative cron job(s) from defineApi.crons`);
    return;
  }

  if (!cronsFile) return;

  const importModule =
    params.importModule ??
    ((path: string) => import(path) as Promise<{ default?: { getJobs?: () => CronJobLike[] } }>);

  try {
    const cronsModule = await importModule(cronsFile);
    const cronsBuilder = cronsModule.default;
    if (!cronsBuilder?.getJobs) {
      return;
    }

    const jobs = cronsBuilder.getJobs();
    log.log(`[crons] getJobs() returned ${jobs.length} job(s)`);
    if (jobs.length === 0) {
      log.warn(
        `[crons] crons.ts found but 0 jobs. Possible cause: cronJobs() builder calls (.interval/.daily/.cron) did not execute.`,
      );
      log.warn(
        `[crons] Check if crons.ts correctly calls crons.interval()/daily()/cron() before export default.`,
      );
    }

    for (const job of jobs) {
      params.scheduler.cron(
        job.name,
        job.pattern,
        createDeclarativeCronHandler({
          scheduler: params.scheduler,
          job,
          mutationMap: params.mutationMap,
          internalProcedureMap: params.internalProcedureMap,
          mutationProcedureMap: params.mutationProcedureMap,
          buildCtx: params.buildCtx,
          parseArgs: params.parseArgs,
          executeWithTimeout: params.executeWithTimeout,
          log,
        }),
      );
    }

    log.log(`[crons] Loaded ${jobs.length} declarative cron job(s) from crons.ts`);
  } catch (e: any) {
    log.error(`[crons] Failed to load crons.ts:`, e?.message);
  }
}
